/**
 * cart-coupon-drawer.js
 * Coupon selection drawer for the cart page.
 *
 * Exposed as:  window.CartCoupon
 * Also exposes backward-compat globals: openCouponDrawer(), closeCouponDrawer(),
 *   addCouponCode(), applyCoupons()
 *
 * Coupon operations are fully independent from points.
 * All lock/unlock calls go through CartLockManager (targeted methods only).
 * After each operation: fetchTimer() + CartUtils.refreshCart() to get live
 * discount values and subtotal from Shopify's /cart.js.
 *
 * Depends on:  window.CartLockManager (cart-lock-manager.js)
 *              window.CartTimer       (cart-timer.js)
 *              window.CartUtils       (cart-timer.js)
 */

(function (global) {
  'use strict';

  /* ─── Coupon data (populated from customer metafield on DOMContentLoaded) ─ */
  var COUPON_DATA = [];

  /* ─── Localized strings (loaded from #coupon-drawer data-attrs; English defaults) ─ */
  var _STR = {
    none:        'No coupons available.',
    promoPeriod: 'Valid Period: [start] - [end].',
    minSpend:    'Minimum Spend: [amount]',
    minItem:     'Minimum item [qty]',
    added:        'Added',
    remove:       'Remove [code]',
    invalid:      'This promo code is invalid or not applicable.',
    notStackable: 'The promo code is not stackable.',
  };
  function _loadStrings() {
    var d = document.getElementById('coupon-drawer');
    if (!d) return;
    if (d.dataset.strNone)         _STR.none         = d.dataset.strNone;
    if (d.dataset.strPromoPeriod)  _STR.promoPeriod  = d.dataset.strPromoPeriod;
    if (d.dataset.strMinSpend)     _STR.minSpend     = d.dataset.strMinSpend;
    if (d.dataset.strMinItem)      _STR.minItem      = d.dataset.strMinItem;
    if (d.dataset.strAdded)        _STR.added        = d.dataset.strAdded;
    if (d.dataset.strRemove)       _STR.remove       = d.dataset.strRemove;
    if (d.dataset.strInvalid)      _STR.invalid      = d.dataset.strInvalid;
    if (d.dataset.strNotStackable) _STR.notStackable = d.dataset.strNotStackable;
  }

  /* ─── Which error to show for a rejected code ─────────────────────────────────
     Mirrors _rejectionReason in cart-promo-drawer.js — see the note there on why
     this is a heuristic. Shopify reports `applicable: false` with no reason, so an
     accepted code elsewhere in the same response is taken as the likely blocker. */
  function _rejectionReason(notApplicable) {
    var applicable = (notApplicable && notApplicable.applicableCodes) || [];
    return applicable.length > 0 ? _STR.notStackable : _STR.invalid;
  }

  /* ─── State ──────────────────────────────────────────────────────────────── */
  var _applied        = [];             // array of { code, title, desc }
  var _isLocking      = false;
  // Codes that originated from the CRM loyalty metafield. Snapshotted at load
  // time so a manually-typed code (which addCode() pushes into COUPON_DATA) is
  // never mistaken for a CRM coupon. When any of these is applied, the manual
  // input row + coupon list are locked (see _updateLockState).
  var _metafieldCodes = new Set();
  // Plain Shopify discount codes typed into the manual input. The CRM knows
  // nothing about these, so they are absent from get-member-point-voucher-timer's
  // coupon_codes — and syncShopifyDiscounts is a FULL replacement, which would
  // strip them on the next lock/unlock of anything else. Tracked here so
  // CartLockManager can union them into every sync. Seeded on boot from the live
  // cart so codes survive a page reload.
  var _manualCodes = new Set();

  // Shopify canonicalises discount-code casing (a typed "T10OFF" comes back as
  // "T10off"), so membership is always tested case-insensitively.
  function _isManual(code) { return _manualCodes.has(String(code).toUpperCase()); }

  /* ─── DOM helpers ────────────────────────────────────────────────────────── */
  function _el(sel) { return document.querySelector(sel); }

  /* ─── Current cart merchandise subtotal (cents) ──────────────────────────────
     Read from the [data-cart-subtotal] element that CartUtils.refreshCart keeps
     in sync with /cart.js (items_subtotal_price). Used to hide coupons whose
     minimum-spend requirement the cart doesn't meet. Returns null if the element
     isn't present, in which case min-spend coupons are shown (fail-open). */
  function _getCartSubtotalCents() {
    var el = _el('[data-cart-subtotal]');
    if (!el) return null;
    var cents = el.getAttribute('data-cart-subtotal-cents');
    if (cents == null || cents === '') cents = el.getAttribute('data-cart-total-cents');
    if (cents == null || cents === '') return null;
    var n = parseInt(cents, 10);
    return isNaN(n) ? null : n;
  }

  /* A min-spend coupon is eligible only when the cart subtotal meets it. Coupons
     with no minSpend (or an unknown subtotal) are always eligible. minSpend is in
     dollars; subtotal is in cents. An already-applied coupon stays visible so it
     can still be unchecked/removed even if the cart later dips below the minimum. */
  function _meetsMinSpend(coupon) {
    if (coupon.minSpend == null) return true;
    if (_applied.some(function (c) { return c.code === coupon.code; })) return true;
    var subtotalCents = _getCartSubtotalCents();
    if (subtotalCents == null) return true;
    return subtotalCents >= Math.round(coupon.minSpend * 100);
  }

  /* ─── Refresh the header mini-cart drawer after a coupon change ──────────────
     Coupons are applied on the cart page, but the header cart drawer
     (#cart-drawer-main) caches its own subtotal + line-item markup and would
     otherwise show stale totals until the next full page load. showHeaderCart
     with update:true re-fetches `cart?view=json` and swaps in fresh content;
     from_cart:true keeps the drawer from popping open (silent refresh).
     CartUtils.refreshCart() already covers the cart page itself. */
  function _refreshHeaderCartDrawer() {
    if (global.$360 && typeof global.$360.showHeaderCart === 'function') {
      try {
        global.$360.showHeaderCart({ update: true, from_cart: true });
      } catch (e) {
        console.error('[CartCoupon] header cart refresh failed:', e);
      }
    }
  }

  /* ─── Transform metafield coupon array → internal shape ─────────────────── */
  // Metafield array: [code, title, value, type, startDate, endDate, statusCode, applicableNote, minSpend, minQty, titleZh]
  // Frontend only needs code + title + dates + min requirements for display.
  // Convert a "DD-MM-YY" date string into "DD/MM/YYYY" (e.g. "16-07-26" -> "16/07/2026").
  function _formatCouponDate(str) {
    if (str == null || str === '') return str;
    var parts = String(str).split('-');
    if (parts.length !== 3) return str;
    var dd = parts[0], mm = parts[1], yy = parts[2];
    var year = yy.length === 2 ? '20' + yy : yy;
    return dd + '/' + mm + '/' + year;
  }

  /* The CRM ships a Traditional Chinese coupon title at index 10, and leaves it
     blank when the coupon hasn't been translated — fall back to the English one. */
  function _isZhLocale() {
    var loc = (global.Shopify && global.Shopify.locale) || document.documentElement.lang || 'en';
    return String(loc).toLowerCase().indexOf('zh') === 0;
  }

  function _couponTitle(arr) {
    var zh = arr[10];
    if (_isZhLocale() && zh != null && String(zh).trim() !== '') return zh;
    return arr[1];
  }

  function _transformMetaCoupon(arr) {
    var rawSpend = arr[8] != null && arr[8] !== '' ? parseFloat(arr[8]) : null;
    var rawQty   = arr[9] != null && arr[9] !== '' ? parseInt(arr[9], 10) : null;
    var note     = arr[7] != null && arr[7] !== '' ? arr[7] : null;
    return {
      code:           arr[0],
      title:          _couponTitle(arr),
      desc:           _STR.promoPeriod.replace('[start]', _formatCouponDate(arr[4])).replace('[end]', _formatCouponDate(arr[5])),
      applicableNote: note,
      minSpend:       (rawSpend !== null && !isNaN(rawSpend)) ? rawSpend : null,
      minQty:         (rawQty   !== null && !isNaN(rawQty))   ? rawQty   : null,
    };
  }

  /* ─── Load coupons from customer metafield DOM attribute ─────────────────── */
  function _loadFromMetafield() {
    var ctxEl = document.querySelector('[data-loyalty-customer-id]');
    if (!ctxEl) return;

    var raw = ctxEl.getAttribute('data-loyalty-coupons');
    if (!raw || raw === 'null') return;

    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        COUPON_DATA = parsed.map(_transformMetaCoupon);
        _metafieldCodes = new Set(COUPON_DATA.map(function (c) { return c.code; }));
      }
    } catch (e) {
      console.error('[CartCoupon] Failed to parse coupons metafield:', e);
    }
  }

  function loadCoupons(data) {
    COUPON_DATA = Array.isArray(data) ? data : [];
    _metafieldCodes = new Set(COUPON_DATA.map(function (c) { return c.code; }));
  }

  /* ─── Scroll lock ─────────────────────────────────────────────────────────────
     overflow:hidden holds on desktop but not against touch scrolling in iOS
     Safari, so on coarse pointers touchmove outside the drawer is cancelled
     outright — the drawer's own scroll area keeps working, and its
     overscroll-behavior:contain stops the gesture chaining out. Anything that
     can't be cancelled (Safari's own scroll-into-view on focus) is snapped back
     to the offset the drawer opened at.

     The drawer is position:fixed, which resolves against the layout viewport. When
     the iOS keyboard opens for #coupon-code-input the visual viewport shrinks, so
     the drawer would sit behind the keyboard and Safari would scroll the document
     to reveal the field. --kb-inset lifts the drawer by the keyboard height and
     the CSS caps its max-height to match. */
  var _lockY       = 0;
  var _touchLocked = false;
  var _vv          = global.visualViewport || null;

  function _pinToVisualViewport() {
    var drawer = document.getElementById('coupon-drawer');
    if (!drawer || !_vv) return;
    // Slice of the layout viewport the visual viewport no longer covers.
    var inset = Math.max(0, global.innerHeight - _vv.height - _vv.offsetTop);
    drawer.style.setProperty('--kb-inset', inset + 'px');
  }

  function _blockTouchMove(e) {
    var t = e.target;
    if (t && t.closest && t.closest('#coupon-drawer')) return;
    if (e.cancelable) e.preventDefault();
  }

  /* iOS Safari scrolls the document to centre a focused input, even one inside a
     position:fixed drawer. Put it back where the drawer opened. */
  function _snapDocScroll() {
    if (global.scrollY === _lockY) return;
    // Instant — the theme's html{scroll-behavior:smooth} would animate this.
    var docEl = document.documentElement;
    docEl.style.scrollBehavior = 'auto';
    global.scrollTo(0, _lockY);
    docEl.style.scrollBehavior = '';
  }

  function _lockScroll() {
    _lockY = global.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    _touchLocked = global.matchMedia('(pointer: coarse)').matches;
    if (!_touchLocked) return;
    document.addEventListener('touchmove', _blockTouchMove, { passive: false });
    global.addEventListener('scroll', _snapDocScroll);
    if (_vv) {
      _vv.addEventListener('resize', _pinToVisualViewport);
      _vv.addEventListener('scroll', _pinToVisualViewport);
      _pinToVisualViewport();
    }
  }

  function _unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (!_touchLocked) return;
    var drawer = document.getElementById('coupon-drawer');
    document.removeEventListener('touchmove', _blockTouchMove, { passive: false });
    global.removeEventListener('scroll', _snapDocScroll);
    if (_vv) {
      _vv.removeEventListener('resize', _pinToVisualViewport);
      _vv.removeEventListener('scroll', _pinToVisualViewport);
    }
    if (drawer) drawer.style.removeProperty('--kb-inset');
    _touchLocked = false;
  }

  /* ─── Drawer open / close ────────────────────────────────────────────────── */
  function open() {
    var drawer  = document.getElementById('coupon-drawer');
    var overlay = document.getElementById('coupon-drawer-overlay');
    if (!drawer || !overlay) return;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    _lockScroll();
    // Clear any error left over from a previous attempt.
    var errorEl = _el('#coupon-drawer .coupon-code-error');
    var input   = document.getElementById('coupon-code-input');
    if (errorEl) errorEl.style.display = 'none';
    if (input)   input.classList.remove('is-error');
    _renderList();
    _syncDrawerLocks();
  }

  /* ─── Drawer lock state ───────────────────────────────────────────────────────
     Only one coupon may ever be applied. Three conditions grey out + disable
     parts of the drawer:

       1. CRM coupon applied — a coupon from the loyalty metafield is already on
          the cart. Both the input row AND the whole coupon list lock. The user
          removes it via the order-summary tag ×; the drawer re-enables on next
          open(). Manually-typed codes never trigger this (see _metafieldCodes).
       2. A list coupon is checked — lock the manual input row and every OTHER
          list item, so a second coupon can't be selected. The checked item stays
          interactive so it can be unchecked.
       3. A promo code is being typed — lock the whole coupon list.

     Re-evaluated on open(), on any checkbox change, and on input in the field. */
  function _hasMetafieldCouponApplied() {
    return _applied.some(function (c) { return _metafieldCodes.has(c.code); });
  }

  function _syncDrawerLocks() {
    var inputRow       = _el('.coupon-drawer__input-row');
    var couponsSection = _el('.coupon-drawer__coupons-section');
    var input          = document.getElementById('coupon-code-input');
    var addBtn         = _el('.coupon-drawer__add-btn');
    var items          = document.querySelectorAll('.coupon-drawer__item');

    var crmApplied   = _hasMetafieldCouponApplied();
    var anyChecked   = document.querySelectorAll('.coupon-drawer__checkbox:checked').length > 0;
    var inputHasText = input ? input.value.trim() !== '' : false;

    // Manual input row: locked when a CRM coupon is applied OR a list coupon is
    // currently selected.
    var lockInput = crmApplied || anyChecked;
    if (inputRow) inputRow.classList.toggle('is-disabled', lockInput);
    if (input)  input.disabled  = lockInput;
    if (addBtn) addBtn.disabled = lockInput;

    // Coupon list: fully locked when a CRM coupon is applied OR a promo code is
    // being typed.
    var lockList = crmApplied || inputHasText;
    if (couponsSection) couponsSection.classList.toggle('is-disabled', lockList);

    // When a list coupon is selected (and the list isn't fully locked), disable
    // every OTHER item so only one coupon can be chosen; keep the checked one live.
    items.forEach(function (item) {
      var cb = item.querySelector('.coupon-drawer__checkbox');
      var disableItem = !lockList && anyChecked && cb && !cb.checked;
      item.classList.toggle('is-disabled', disableItem);
      if (cb) cb.disabled = disableItem;
    });
  }

  function close() {
    var drawer  = document.getElementById('coupon-drawer');
    var overlay = document.getElementById('coupon-drawer-overlay');
    var input   = document.getElementById('coupon-code-input');
    // Dismiss the keyboard before unlocking, so the visual viewport is back to
    // full height by the time the scroll position is restored.
    if (input) input.blur();
    if (drawer)  drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    _unlockScroll();
  }

  /* ─── List rendering ─────────────────────────────────────────────────────── */
  function _renderList() {
    var list = _el('.coupon-drawer__list');
    if (!list) return;
    list.innerHTML = '';

    // Hide coupons whose minimum-spend requirement the cart doesn't meet.
    var visibleCoupons = COUPON_DATA.filter(_meetsMinSpend);

    if (visibleCoupons.length === 0) {
      var empty = document.createElement('li');
      empty.className   = 'coupon-drawer__empty';
      empty.textContent = _STR.none;
      list.appendChild(empty);
      return;
    }

    visibleCoupons.forEach(function (coupon) {
      var isSelected = _applied.some(function (c) { return c.code === coupon.code; });

      var fmt = global.CartUtils ? global.CartUtils.formatMoney : function (c) { return 'RM' + (c / 100).toFixed(2); };
      var descParts = [coupon.desc];
      if (coupon.applicableNote != null) descParts.push(coupon.applicableNote);
      if (coupon.minSpend != null) descParts.push(_STR.minSpend.replace('[amount]', fmt(Math.round(coupon.minSpend * 100))));
      if (coupon.minQty != null) descParts.push(_STR.minItem.replace('[qty]', coupon.minQty));
      var descText = descParts.join(' ');

      var titleHtml = coupon.title
        ? '<p class="coupon-drawer__item-title p2 bold">' + coupon.title + '</p>'
        : '';

      var li = document.createElement('li');
      li.className = 'coupon-drawer__item' + (isSelected ? ' is-selected' : '');
      li.innerHTML =
        '<label class="coupon-drawer__label">' +
          '<input type="checkbox" class="coupon-drawer__checkbox"' +
            ' value="' + coupon.code + '"' + (isSelected ? ' checked' : '') + '>' +
          '<div class="coupon-drawer__checkbox-custom"></div>' +
          '<div class="coupon-drawer__item-content">' +
            titleHtml +
            '<div class="coupon-drawer__code-pill p4">' + coupon.code + '</div>' +
            '<p class="coupon-drawer__item-desc p4">' + descText + '</p>' +
          '</div>' +
        '</label>';

      var cb = li.querySelector('.coupon-drawer__checkbox');
      if (cb) {
        cb.addEventListener('change', function () {
          li.classList.toggle('is-selected', cb.checked);
          _syncDrawerLocks();
        });
      }

      list.appendChild(li);
    });
  }

  /* ─── Manual code entry ("Add" button) ──────────────────────────────────── */
  // A manually-typed code is a plain Shopify discount code, NOT a CRM member
  // coupon — lockCoupon() (lock-member-coupon) rejects it and would surface a
  // false error. Apply it the same way the guest promo drawer does: straight
  // through syncShopifyDiscounts() (Storefront cartDiscountCodesUpdate), which
  // works for any valid discount. Since that call is a FULL replacement, seed
  // the desired list from the live cart so applied coupons AND the POINTS
  // discount are preserved, then add the new code. Treat the code as rejected
  // only when Shopify explicitly lists it as not applicable.
  async function addCode() {
    var input   = document.getElementById('coupon-code-input');
    // Scoped to #coupon-drawer — the guest promo drawer (cart-promo-drawer.liquid)
    // renders an identically-classed element.
    var errorEl = _el('#coupon-drawer .coupon-code-error');
    if (!input || !global.CartLockManager) return;

    var code = input.value.trim().toUpperCase();
    if (!code) return;

    // Already applied — no-op. Confirm against the live cart first: if _applied and
    // Shopify have diverged (a full-replacement sync dropped the code while the tag
    // stayed on screen) a blind return would leave the user unable to re-add it.
    if (_applied.some(function (c) { return c.code === code; })) {
      var liveCodes = [];
      try {
        var liveRes = await fetch('/cart.js');
        if (liveRes.ok) {
          var liveCart = await liveRes.json();
          liveCodes = (liveCart.discount_codes || []).map(function (dc) {
            return dc && dc.code ? String(dc.code).toUpperCase() : '';
          });
        }
      } catch (e) { /* can't confirm — fall through and re-apply */ }
      if (liveCodes.indexOf(code) !== -1) {
        input.value = '';
        return;
      }
      // Diverged — drop the stale entry and re-apply below.
      _applied = _applied.filter(function (c) { return c.code !== code; });
    }

    var addBtn     = _el('.coupon-drawer__add-btn');
    var addBtnText = addBtn ? addBtn.textContent : '';
    if (addBtn) { addBtn.disabled = true; addBtn.textContent = _STR.added; }
    if (errorEl) errorEl.style.display = 'none';
    input.classList.remove('is-error');

    try {
      // Seed the full-replacement list from the live cart (applied coupons +
      // POINTS-xxx discount), then append the typed code.
      var desired = [code];
      try {
        var cartRes = await fetch('/cart.js');
        if (cartRes.ok) {
          var cart = await cartRes.json();
          (cart.discount_codes || []).forEach(function (dc) {
            var c = dc && dc.code ? String(dc.code) : '';
            if (c && c.toUpperCase() !== code && desired.indexOf(c) === -1) desired.push(c);
          });
        }
      } catch (e) { /* fall back to just the typed code */ }

      // Register as manual BEFORE syncing. syncShopifyDiscounts handles its own
      // not-applicable branch inline, and that branch checks _manualCodes to
      // decide whether to call unlock-member-coupon. Registering afterwards would
      // leave the code unknown at that moment, so a rejected plain Shopify code
      // would be sent to the CRM and come back "CRM Coupon Lock Failed".
      _manualCodes.add(code);

      var notApplicable = await global.CartLockManager.syncShopifyDiscounts(desired);
      // syncShopifyDiscounts returns [] on network failure too — treat the code
      // as rejected only when it is explicitly listed as not applicable.
      var rejected = Array.isArray(notApplicable) &&
        notApplicable.map(function (c) { return String(c).toUpperCase(); }).indexOf(code) !== -1;

      if (rejected) {
        _manualCodes.delete(code);   // deregister — Shopify won't honour it
        input.classList.add('is-error');
        if (errorEl) {
          errorEl.textContent   = _rejectionReason(notApplicable);
          errorEl.style.display = 'block';
        }
      } else {
        input.value = '';
        // Add to COUPON_DATA if not already present (for future list rendering)
        if (!COUPON_DATA.some(function (c) { return c.code === code; })) {
          COUPON_DATA.push({ code: code, title: code, desc: '', applicableNote: null, minSpend: null, minQty: null });
        }
        var coupon = COUPON_DATA.find(function (c) { return c.code === code; });
        _applied.push(coupon || { code: code, title: code, desc: '', applicableNote: null, minSpend: null, minQty: null });
        _renderTags();
        if (global.CartUtils) await global.CartUtils.refreshCart();
        _refreshHeaderCartDrawer();
        close();
        document.dispatchEvent(new CustomEvent('cart:coupon-applied', { detail: _applied.slice() }));
      }
    } finally {
      if (addBtn) { addBtn.disabled = false; addBtn.textContent = addBtnText; }
    }
  }

  /* ─── Apply selected coupons (Apply button) ──────────────────────────────── */
  // Diffs current _applied vs drawer selection:
  //   toUnlock = was applied but now unchecked → batch unlock
  //   toLock   = newly checked but not yet applied → lock one by one
  async function apply() {
    if (_isLocking) return;

    // A code typed into the manual field but never "Add"-ed would otherwise be
    // silently dropped here (apply() only reads checkboxes). Route it through
    // addCode(), which owns the plain-Shopify-discount path and its error UI.
    var manualInput = document.getElementById('coupon-code-input');
    if (manualInput && manualInput.value.trim() !== '') {
      await addCode();
      return;
    }

    var checked  = document.querySelectorAll('.coupon-drawer__checkbox:checked');
    var selected = [];
    checked.forEach(function (cb) {
      var coupon = COUPON_DATA.find(function (c) { return c.code === cb.value; });
      if (coupon) selected.push(coupon);
    });

    var prevSorted = _applied.map(function (c) { return c.code; }).sort().join(',');
    var newSorted  = selected.map(function (c) { return c.code; }).sort().join(',');
    if (prevSorted === newSorted) { close(); return; }

    _isLocking = true;
    try {
      var appliedCodes  = _applied.map(function (c) { return c.code; });
      var selectedCodes = selected.map(function (c) { return c.code; });

      var toUnlockCodes = appliedCodes.filter(function (code) {
        return selectedCodes.indexOf(code) === -1;
      });
      var toLock = selected.filter(function (c) {
        return appliedCodes.indexOf(c.code) === -1;
      });

      // Batch unlock deselected coupons. Manual codes were never CRM-locked —
      // dropping them from _manualCodes is enough; the next syncShopifyDiscounts
      // (full replacement) removes them from the Shopify cart.
      var toUnlockCrm = toUnlockCodes.filter(function (code) {
        if (_isManual(code)) { dropManualCode(code); return false; }
        return true;
      });
      if (toUnlockCrm.length > 0) {
        await global.CartLockManager.unlockCoupons(toUnlockCrm);
      }

      // Lock newly selected coupons one by one; track which succeed
      var successfullyLocked = [];
      for (var i = 0; i < toLock.length; i++) {
        // Manual codes have no CRM record — lock-member-coupon would reject them.
        // Re-selecting one just re-registers it for the discount-code sync.
        if (!_metafieldCodes.has(toLock[i].code)) {
          _manualCodes.add(toLock[i].code.toUpperCase());
          successfullyLocked.push(toLock[i]);
          continue;
        }
        var ok = await global.CartLockManager.lockCoupon(toLock[i].code);
        if (ok) successfullyLocked.push(toLock[i]);
      }

      // Optimistic local state — will be overridden by server truth from fetchTimer
      var remaining = _applied.filter(function (c) {
        return toUnlockCodes.indexOf(c.code) === -1;
      });
      _applied = remaining.concat(successfullyLocked);
      _renderTags();

      var timerRes = await global.CartLockManager.fetchTimer();
      _syncFromTimer(timerRes);
      if (global.CartUtils) await global.CartUtils.refreshCart();
      close();
      document.dispatchEvent(new CustomEvent('cart:coupon-applied', { detail: _applied.slice() }));
    } finally {
      _isLocking = false;
    }
  }

  /* ─── Sync coupon + points state from timer response ────────────────────── */
  // After every fetchTimer() call, override _applied with the server's coupon list.
  // Also syncs points display — removing a points-generated coupon auto-unlocks
  // the points on the backend, and CartPoints.syncFromTimer handles that.
  // If timerRes is null (network error) we keep optimistic state in both modules.
  function _syncFromTimer(timerRes) {
    if (timerRes && timerRes.data) {
      restoreFromCodes(timerRes.data.coupon_codes || []);
    }
    if (global.CartPoints && typeof global.CartPoints.syncFromTimer === 'function') {
      global.CartPoints.syncFromTimer(timerRes);
    }
  }

  /* ─── Render removable tags ──────────────────────────────────────────────── */
  function _renderTags() {
    var container = _el('[data-coupon-tags]');
    if (!container) return;
    var pointsTag = container.querySelector('[data-points-tag]');
    container.innerHTML = '';

    _applied.forEach(function (coupon) {
      // POINTS-xxx codes are generated by the points lock — never shown to the user.
      if (coupon.code.indexOf('POINTS') === 0) return;

      var tag = document.createElement('div');
      tag.className = 'cart-applied-tag';
      tag.setAttribute('data-coupon-tag', coupon.code);
      tag.innerHTML =
        '<p class="p3">' + coupon.code + '</p>' +
        '<button type="button" class="cart-applied-tag__remove" aria-label="' + _STR.remove.replace('[code]', coupon.code) + '">&#215;</button>';
      tag.querySelector('button').addEventListener('click', function () {
        _removeOne(coupon.code);
      });
      container.appendChild(tag);
    });

    if (pointsTag) container.appendChild(pointsTag);
  }

  /* ─── Remove a single coupon by code (tag × button) ─────────────────────── */
  async function _removeOne(code) {
    if (_isManual(code)) dropManualCode(code);        // never CRM-locked
    else await global.CartLockManager.unlockCoupons([code]);
    // Optimistic — removed from local list immediately
    _applied = _applied.filter(function (c) { return c.code !== code; });
    _renderTags();
    // Override with server truth (in case backend auto-locked something else)
    var timerRes = await global.CartLockManager.fetchTimer();
    _syncFromTimer(timerRes);
    if (global.CartUtils) await global.CartUtils.refreshCart();
    _refreshHeaderCartDrawer();
    document.dispatchEvent(new CustomEvent('cart:coupon-removed', { detail: { code: code } }));
  }

  /* ─── Remove all applied coupons ────────────────────────────────────────── */
  // silent=true: called by CartTimer on expiry — skip fetchTimer/refreshCart
  //              (cart is about to be cleared and reloaded).
  // silent=false (default): user-triggered — full refresh.
  async function removeApplied(silent) {
    if (_applied.length === 0) return;
    var codes = _applied.map(function (c) { return c.code; });
    var crmCodes = codes.filter(function (code) {
      if (_isManual(code)) { dropManualCode(code); return false; }
      return true;
    });
    if (crmCodes.length > 0) await global.CartLockManager.unlockCoupons(crmCodes);
    _applied = [];
    _renderTags();
    if (!silent) {
      var timerRes = await global.CartLockManager.fetchTimer();
      _syncFromTimer(timerRes);
      if (global.CartUtils) await global.CartUtils.refreshCart();
      _refreshHeaderCartDrawer();
    }
    document.dispatchEvent(new CustomEvent('cart:coupon-removed'));
  }

  /* ─── Restore applied coupons from server state (page load) ─────────────── */
  // Called by cart-points.js _init() after get-member-point-voucher-timer.
  // Only sets internal state + renders tags — no API calls.
  // POINTS-xxx codes are excluded: they belong to the points flow and are unlocked
  // via CartPoints.removeApplied(), not via unlockCoupons().
  function restoreFromCodes(codes) {
    // The CRM list is authoritative for member coupons only. Manual codes are
    // invisible to it, so union them back in or the tag would vanish on every
    // fetchTimer even though the discount is still live on the Shopify cart.
    var merged = codes.filter(function (code) { return code.indexOf('POINTS') !== 0; });
    var mergedUpper = merged.map(function (c) { return String(c).toUpperCase(); });
    _manualCodes.forEach(function (code) {
      if (mergedUpper.indexOf(code) === -1) merged.push(code);
    });
    _applied = merged.map(function (code) {
      var found = COUPON_DATA.find(function (c) { return c.code === code; });
      return found || { code: code, title: code, desc: '', applicableNote: null, minSpend: null, minQty: null };
    });
    _renderTags();
  }

  /* ─── Manual-code registry (used by CartLockManager) ─────────────────────── */
  function getManualCodes() { return Array.from(_manualCodes); }

  // Called by syncShopifyDiscounts when Shopify reports a code as not applicable
  // (e.g. it expired, or the cart no longer meets its conditions) so it stops
  // being re-sent on every subsequent sync.
  function dropManualCode(code) { _manualCodes.delete(String(code).toUpperCase()); }

  /* ─── Seed manual codes from the live Shopify cart (page load) ───────────── */
  // Any discount code on the cart that is neither a CRM member coupon nor a
  // POINTS-xxx code must have come from the manual input in an earlier session.
  // Kicked off immediately (not on DOMContentLoaded) so it is in flight before
  // cart-points' _init() reaches its first fetchTimer(); whenReady() lets
  // CartLockManager await it. Membership is decided here rather than at fetch
  // time because _metafieldCodes is only populated once the DOM is parsed.
  var _seedPromise = (async function () {
    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return [];
      var cart = await res.json();
      return (cart.discount_codes || [])
        .map(function (dc) { return dc && dc.code ? String(dc.code) : ''; })
        .filter(function (code) { return code && code.indexOf('POINTS') !== 0; });
    } catch (e) {
      console.error('[CartCoupon] Failed to seed manual codes:', e);
      return [];
    }
  })();

  var _seeded = false;
  async function whenReady() {
    var codes = await _seedPromise;
    // _metafieldCodes is filled in the DOMContentLoaded handler below; classifying
    // before that would mark every CRM coupon as manual.
    if (document.readyState === 'loading') {
      await new Promise(function (resolve) {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      });
    }
    if (_seeded) return;
    _seeded = true;
    codes.forEach(function (code) {
      if (!_metafieldCodes.has(code)) _manualCodes.add(code.toUpperCase());
    });
  }

  /* ─── Boot ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    _loadStrings();
    _loadFromMetafield();
    // _metafieldCodes is now known — resolve the manual-code seed against it.
    whenReady();

    // Typing a promo code locks the coupon list (and clearing it unlocks) —
    // only one coupon may be applied at a time.
    var input = document.getElementById('coupon-code-input');
    if (input) input.addEventListener('input', _syncDrawerLocks);
  });

  /* ─── Exports ────────────────────────────────────────────────────────────── */
  global.CartCoupon = {
    open:             open,
    close:            close,
    apply:            apply,
    removeApplied:    removeApplied,
    loadCoupons:      loadCoupons,
    restoreFromCodes: restoreFromCodes,
    whenReady:        whenReady,
    getManualCodes:   getManualCodes,
    dropManualCode:   dropManualCode,
    getApplied:       function () { return _applied.slice(); },
    hasApplied:       function () { return _applied.length > 0; },
    hasMetafieldCouponApplied: _hasMetafieldCouponApplied,
  };

  global.openCouponDrawer  = open;
  global.closeCouponDrawer = close;
  global.addCouponCode     = addCode;
  // Serialize points then coupons. Each op re-syncs the full discount-code list from the
  // server (fetchTimer → syncShopifyDiscounts is a full replacement); awaiting prevents
  // two concurrent replacements from clobbering each other's codes.
  global.applyCoupons      = async function () {
    // Loading state — swap the CTA button text for the theme's animated SVG (.btn-loading)
    // for the whole op (points + coupons), regardless of which changed.
    var btn = _el('.coupon-drawer__apply-btn') || _el('.coupon-drawer__cta button');
    var btnHTML = null;
    if (btn) {
      btnHTML = btn.innerHTML;
      btn.style.height = btn.offsetHeight + 'px';
      btn.innerHTML =
        '<span class="btn-loading">' +
          '<svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
            '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
          '</svg>' +
        '</span>';
      btn.disabled = true;
    }
    try {
      if (global.CartPoints) await global.CartPoints.applyPoints();
      await apply();
      _refreshHeaderCartDrawer();
    } finally {
      if (btn && btnHTML !== null) {
        btn.innerHTML = btnHTML;
        btn.disabled = false;
        btn.style.height = '';
      }
    }
  };

})(window);

